import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRouterNavigate } from './links/useEntityLinkProps';
import { realLinkProps } from './links/realLinkProps';
import {
  Badge,
  Button,
  Field,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
  type BadgeProps,
} from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import {
  ExportError,
  useConversions,
  useDownloadConversion,
  useExtractDr15FromUpload,
  useJournals,
  useUploads,
  useRemovedJournals,
  useRestoreJournal,
} from '../services/conversionsApi';
import { CalendarDateInput } from './dates/CalendarDateInput';
import { dr15JournalHandoffUrl } from '../lib/journalHandoff';
import { formatLocalDateTime } from '../lib/provenance';
import { isDownloadable, saveDownloadedFile } from '../lib/conversionHistory';
import {
  WORK_STATUSES,
  deriveWorkItems,
  filterWorkItems,
  isWorkStatus,
  type WorkItem,
  type WorkStatus,
} from '../lib/workItems';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
  narrow: { width: '220px' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  empty: { padding: tokens.spacingVerticalXXL, textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

const STATUS_COLOR: Record<WorkStatus, BadgeProps['color']> = {
  uploaded: 'informative',
  draft: 'subtle',
  proposed: 'brand',
  unconfirmed: 'warning',
  confirmed: 'success',
  exported: 'success',
  corrected: 'subtle',
};

/**
 * The client's Work (docs/work-history.md §1–§2): every journal, every DR-15 or POS upload no journal has used yet, and
 * every bank file produced, newest activity first, each with ONE derived status and the way to carry on with it. The
 * filters are in the URL (the fleet view-state rule), so Back from a journal returns to the same list.
 */
export default function WorkTab({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Every screen opened from a row gets this list's own URL, so its Back (BackToWork) returns here, filters and all.
  const fromWork = { state: { backTo: location.pathname + location.search } };
  // A row's way into a journal is a real link (fleet ruling 2026-10-29): right-click opens it in a new tab. A plain
  // click carries this list's URL as above; a new tab cannot, and its Back to Work opens the plain list.
  const follow = useRouterNavigate(fromWork.state);
  const [searchParams, setSearchParams] = useSearchParams();

  const statusParam = searchParams.get('status');
  const status = isWorkStatus(statusParam) ? statusParam : undefined;
  // "Removed" is a filter, not a status (ptc-docs plan 2026-11-02 §3): it lists the removed journals, each with Restore.
  const showRemoved = statusParam === REMOVED_FILTER;
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const setFilter = (key: 'status' | 'from' | 'to', value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  const journals = useJournals(clientId);
  // Removed uploads too: a journal still names a source that was removed after it was made.
  const uploads = useUploads(clientId, true);
  const conversions = useConversions(clientId);
  const download = useDownloadConversion();
  const extract = useExtractDr15FromUpload();
  const [actionError, setActionError] = useState<string | null>(null);

  const items = useMemo(
    () =>
      journals.data
        ? deriveWorkItems({
            clientId,
            journals: journals.data,
            uploads: uploads.data ?? [],
            conversions: conversions.data ?? [],
          })
        : [],
    [clientId, journals.data, uploads.data, conversions.data],
  );
  const shown = filterWorkItems(items, { status, from, to });
  const filtered = !!(status || from || to);

  const journalLink = (journalId: string) =>
    realLinkProps(`/journals?${new URLSearchParams({ clientId, journalId }).toString()}`, { navigate: follow });

  const onExtract = (item: WorkItem) => {
    const doc = item.upload!;
    setActionError(null);
    extract.mutate(
      { clientId, documentId: doc.id },
      {
        // programmatic-nav: after the extraction, the journal it fills (no element is being followed).
        onSuccess: (result) => navigate(dr15JournalHandoffUrl(doc, result), fromWork),
        onError: (err) => setActionError(err instanceof Error ? err.message : t('clientPage.work.actionFailed')),
      },
    );
  };

  const onDownload = async (item: WorkItem) => {
    setActionError(null);
    try {
      saveDownloadedFile(await download.mutateAsync({ clientId, conversionId: item.id }));
    } catch (err) {
      setActionError(err instanceof ExportError ? err.message : t('clientPage.conversions.downloadFailed'));
    }
  };

  const action = (item: WorkItem) => {
    if (item.kind === 'journal') {
      return (
        <div className={styles.actions}>
          <Button as="a" size="small" {...journalLink(item.id)}>{t('clientPage.work.open')}</Button>
          {item.correctedBy && (
            <Button as="a" size="small" appearance="subtle" {...journalLink(item.correctedBy)}>
              {t('clientPage.work.openCorrection')}
            </Button>
          )}
        </div>
      );
    }
    if (item.kind === 'upload') {
      const doc = item.upload!;
      if (doc.category === 'dr15Return') {
        const pending = extract.isPending && extract.variables?.documentId === doc.id;
        return (
          <Button
            size="small"
            onClick={() => onExtract(item)}
            disabled={extract.isPending}
            icon={pending ? <Spinner size="tiny" /> : undefined}
          >
            {t('uploads.actions.extractDr15')}
          </Button>
        );
      }
      return (
        <Button
          as="a"
          size="small"
          {...realLinkProps(
            `/journals?${new URLSearchParams({ clientId, sourceDocumentId: doc.id, template: 'posSalesPeriod' }).toString()}`,
            { navigate: follow },
          )}
        >
          {t('uploads.actions.prepareManualJournal')}
        </Button>
      );
    }
    return isDownloadable(item.conversion!) ? (
      <Button
        size="small"
        appearance="subtle"
        icon={<ArrowDownloadRegular />}
        disabled={download.isPending}
        onClick={() => void onDownload(item)}
      >
        {t('clientPage.conversions.download')}
      </Button>
    ) : (
      <Text className={styles.muted}>{t('clientPage.conversions.notRetained')}</Text>
    );
  };

  return (
    <div className={styles.section}>
      <div className={styles.filters}>
        <Field label={t('clientPage.work.filterStatus')} className={styles.narrow}>
          <Select value={status ?? ''} onChange={(_e, data) => setFilter('status', data.value)}>
            <option value="">{t('clientPage.work.allStatuses')}</option>
            {WORK_STATUSES.map((s) => (
              <option key={s} value={s}>{t(`clientPage.work.status.${s}`)}</option>
            ))}
            <option value={REMOVED_FILTER}>{t('clientPage.work.removed.option')}</option>
          </Select>
        </Field>
        <Field label={t('clientPage.work.filterFrom')} className={styles.narrow}>
          <CalendarDateInput value={from} onChange={(v) => setFilter('from', v)} />
        </Field>
        <Field label={t('clientPage.work.filterTo')} className={styles.narrow}>
          <CalendarDateInput value={to} onChange={(v) => setFilter('to', v)} />
        </Field>
      </div>
      <Text className={styles.muted}>{t('clientPage.work.filterNote')}</Text>

      {actionError && (
        <MessageBar intent="error">
          <MessageBarBody>{actionError}</MessageBarBody>
        </MessageBar>
      )}

      {showRemoved && <RemovedJournals clientId={clientId} />}

      {!showRemoved && journals.isLoading && <Spinner label={t('common.loading')} />}

      {/* Without the journals there is no list worth showing: most rows ARE journals, and an upload would read as
          "no journal yet" when its journal simply did not load. */}
      {!showRemoved && journals.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{t('clientPage.work.journalsFailed')}</MessageBarBody>
        </MessageBar>
      )}
      {!showRemoved && journals.data && uploads.isError && (
        <MessageBar intent="warning">
          <MessageBarBody>{t('clientPage.work.uploadsFailed')}</MessageBarBody>
        </MessageBar>
      )}
      {!showRemoved && journals.data && conversions.isError && (
        <MessageBar intent="warning">
          <MessageBarBody>{t('clientPage.work.conversionsFailed')}</MessageBarBody>
        </MessageBar>
      )}

      {!showRemoved && journals.data && shown.length === 0 && (
        <div className={styles.empty}>
          <Text>{filtered ? t('clientPage.work.emptyFiltered') : t('clientPage.work.empty')}</Text>
        </div>
      )}

      {!showRemoved && journals.data && shown.length > 0 && (
        <Table aria-label={t('clientPage.work.tableLabel')}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('clientPage.work.colStatus')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colSources')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colDate')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colActivity')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colWho')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colAction')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((item) => (
              <TableRow key={`${item.kind}:${item.id}`}>
                <TableCell>
                  <Badge appearance="tint" color={STATUS_COLOR[item.status]}>
                    {t(`clientPage.work.status.${item.status}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {item.sources.length > 0 ? item.sources.join(', ') : (
                    <Text className={styles.muted}>
                      {item.journal?.narration || t('clientPage.work.noSource')}
                    </Text>
                  )}
                </TableCell>
                <TableCell>{item.date || '—'}</TableCell>
                <TableCell>{item.lastActivity ? formatLocalDateTime(item.lastActivity, i18n.language) : '—'}</TableCell>
                <TableCell>{item.who ?? '—'}</TableCell>
                <TableCell>{action(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/** The value of the Status filter that lists removed journals (not a derived status: nothing removed is "in progress"). */
const REMOVED_FILTER = 'removed';

/**
 * Work's "removed" filter (docs/work-history.md §4; ptc-docs plan 2026-11-02 §3): the client's removed journals, kept
 * (soft delete), each saying who removed it and when, with Restore. Restoring is reversible: removing it again is the same.
 */
function RemovedJournals({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const removed = useRemovedJournals(clientId);
  const restore = useRestoreJournal();
  const [restored, setRestored] = useState<string | null>(null);

  if (removed.isLoading) return <Spinner label={t('common.loading')} />;
  if (removed.isError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{t('clientPage.work.removed.failed')}</MessageBarBody>
      </MessageBar>
    );
  }
  const rows = removed.data ?? [];
  return (
    <>
      {restored && (
        <MessageBar intent="success">
          <MessageBarBody>{t('clientPage.work.removed.restored', { journal: restored })}</MessageBarBody>
        </MessageBar>
      )}
      {restore.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{restore.error instanceof Error ? restore.error.message : t('clientPage.work.actionFailed')}</MessageBarBody>
        </MessageBar>
      )}
      {rows.length === 0 ? (
        <div className={styles.empty}>
          <Text>{t('clientPage.work.removed.empty')}</Text>
        </div>
      ) : (
        <Table aria-label={t('clientPage.work.removed.tableLabel')}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('clientPage.work.colDate')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colSources')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.removed.colRemoved')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.work.colAction')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((journal) => {
              const label = journal.narration || journal.journalDate;
              return (
                <TableRow key={journal.id}>
                  <TableCell>{journal.journalDate}</TableCell>
                  <TableCell>
                    <Text className={styles.muted}>{journal.narration || t('clientPage.work.noSource')}</Text>
                  </TableCell>
                  <TableCell>
                    {journal.deletedAt
                      ? t('clientPage.work.removed.by', {
                          name: journal.deletedByName ?? t('clientPage.work.removed.someone'),
                          date: formatLocalDateTime(journal.deletedAt, i18n.language),
                        })
                      : t('clientPage.work.removed.unrecorded')}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled={restore.isPending}
                      aria-label={t('clientPage.work.removed.restoreOf', { journal: label })}
                      onClick={() =>
                        restore.mutate({ clientId, journalId: journal.id }, { onSuccess: () => setRestored(label) })}
                    >
                      {t('clientPage.work.removed.restore')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}
